import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { SessionDetailDialog } from "@/components/support/SessionDetailDialog";
import { ContactsSection } from "@/components/support/ContactsSection";
import { fmtDuration, fmtDate, STATUS_COLOR, PRIORITY_COLOR, BILLING_COLOR } from "@/lib/supportApi";
import { ArrowLeft, Desktop, Plus, Plugs, Ticket } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function CustomerDetail() {
  const { cid } = useParams();
  const { currentOrgId } = useAuth();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [time, setTime] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [allMachines, setAllMachines] = useState([]);
  const [orgMachines, setOrgMachines] = useState([]);
  const [busyMid, setBusyMid] = useState(null);
  const [detailSid, setDetailSid] = useState(null);

  const loadSessions = useCallback(() => {
    if (!currentOrgId) return;
    api.get(`/support/customers/${cid}/sessions?org_id=${currentOrgId}`).then((r) => setSessions(r.data)).catch(() => {});
  }, [cid, currentOrgId]);

  const loadCustomer = useCallback(() => {
    if (!currentOrgId) return;
    api.get(`/support/customers/${cid}?org_id=${currentOrgId}`).then((r) => setC(r.data)).catch(() => {});
  }, [cid, currentOrgId]);

  useEffect(() => {
    if (!currentOrgId) return;
    loadCustomer();
    api.get(`/support/customers/${cid}/tickets?org_id=${currentOrgId}`).then((r) => setTickets(r.data)).catch(() => {});
    api.get(`/support/customers/${cid}/time?org_id=${currentOrgId}`).then((r) => setTime(r.data)).catch(() => {});
    api.get(`/support/customers/${cid}/sessions?org_id=${currentOrgId}`).then((r) => setSessions(r.data)).catch(() => {});
    api.get(`/orgs/${currentOrgId}/machines`).then((r) => setOrgMachines(r.data)).catch(() => {});
  }, [cid, currentOrgId, loadCustomer]);

  // Access-filtered dashboard list = source of truth for authorization + fresh online state.
  const orgMap = {};
  for (const m of orgMachines) orgMap[m.machine_id] = m;

  const openAssign = () => {
    api.get(`/support/machines?org_id=${currentOrgId}`).then((r) => setAllMachines(r.data)).catch(() => {});
    setAssignOpen(true);
  };

  const setMachineCustomer = async (m, customerId) => {
    setBusyMid(m.machine_id);
    try {
      await api.patch(`/support/machines/${m.machine_id}/customer`, { org_id: currentOrgId, customer_id: customerId });
      toast.success(customerId ? "Computer assigned" : "Computer unassigned");
      const r = await api.get(`/support/machines?org_id=${currentOrgId}`);
      setAllMachines(r.data);
      loadCustomer();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update assignment");
    } finally { setBusyMid(null); }
  };

  if (!c) return <div className="text-zinc-500 text-sm">Loading…</div>;

  return (
    <div className="space-y-5" data-testid="customer-detail">
      <button onClick={() => navigate("/helpdesk/customers")} className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white"><ArrowLeft size={15} /> Customers</button>
      <div><h1 className="font-heading text-2xl font-bold tracking-tight">{c.company_name}</h1><div className="text-sm text-zinc-500">{c.status}</div></div>

      <Tabs defaultValue="overview">
        <TabsList className="bg-zinc-900 border border-white/10 h-auto flex-wrap justify-start max-w-full">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="machines">Machines ({c.machines?.length || 0})</TabsTrigger>
          <TabsTrigger value="tickets">Tickets ({tickets.length})</TabsTrigger>
          <TabsTrigger value="time">Time ({time.length})</TabsTrigger>
          <TabsTrigger value="sessions">Sessions ({sessions.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4 space-y-3">
          <ContactsSection customer={c} orgId={currentOrgId} onChanged={loadCustomer} />
          <div className="rounded-xl border border-white/10 bg-zinc-900/60 p-4">
            <h3 className="text-xs uppercase tracking-wider text-zinc-400 mb-2">Notes</h3>
            <div className="text-sm text-zinc-300 whitespace-pre-wrap">{c.notes || "—"}</div>
          </div>
        </TabsContent>

        <TabsContent value="machines" className="mt-4 space-y-3">
          <div className="flex justify-end">
            <Button data-testid="customer-assign-machine-button" onClick={openAssign} size="sm" className="bg-blue-600 hover:bg-blue-500">
              <Plus size={15} weight="bold" className="mr-1.5" /> Assign a computer
            </Button>
          </div>
          <div className="rounded-xl border border-white/10 bg-zinc-900/60 divide-y divide-white/5">
            {(c.machines || []).map((m) => {
              const og = orgMap[m.machine_id];
              const authorized = !!og;                       // only access-filtered machines are connectable
              const online = og ? og.online : m.online;
              const canConnect = authorized && online;
              return (
                <div key={m.machine_id} data-testid={`customer-machine-${m.machine_id}`} className="flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-3">
                  <div className="flex items-center gap-2 text-sm min-w-0 sm:flex-1">
                    <Desktop size={16} className="text-zinc-400 shrink-0" />
                    <span className="truncate">{m.name}</span>
                    <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded border ${online ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10" : "text-zinc-500 border-white/10"}`}>{online ? "online" : "offline"}</span>
                    {!authorized && <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded border text-amber-400 border-amber-500/30 bg-amber-500/10">no access</span>}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      data-testid={`customer-connect-machine-${m.machine_id}`}
                      disabled={!canConnect}
                      title={!authorized ? "You don't have access to this computer" : (!online ? "Computer is offline" : "Start a remote session")}
                      onClick={() => navigate(`/session/${m.machine_id}`)}
                      className={`inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium transition-colors ${canConnect ? "bg-blue-600 hover:bg-blue-500 text-white" : "bg-zinc-800 text-zinc-500 cursor-not-allowed"}`}
                    >
                      <Plugs size={14} weight="bold" /> Connect
                    </button>
                    <button
                      data-testid={`customer-ticket-machine-${m.machine_id}`}
                      onClick={() => navigate(`/helpdesk/tickets?new=1&machine_id=${m.machine_id}&customer_id=${cid}`)}
                      className="inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium border border-white/15 text-zinc-200 hover:bg-white/5 transition-colors"
                    >
                      <Ticket size={14} /> Ticket
                    </button>
                    <button
                      data-testid={`customer-unassign-machine-${m.machine_id}`}
                      disabled={busyMid === m.machine_id}
                      onClick={() => setMachineCustomer({ machine_id: m.machine_id }, null)}
                      className="inline-flex items-center justify-center h-8 px-2 rounded-md text-xs text-zinc-400 hover:text-red-400 transition-colors"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              );
            })}
            {(c.machines || []).length === 0 && <div className="p-6 text-center text-sm text-zinc-500">No computers assigned yet. Use “Assign a computer” above, or assign from a machine's menu on the dashboard.</div>}
          </div>
        </TabsContent>

        <TabsContent value="tickets" className="mt-4">
          <div className="rounded-xl border border-white/10 bg-zinc-900/60 divide-y divide-white/5">
            {tickets.map((t) => (
              <button key={t.ticket_id} onClick={() => navigate(`/helpdesk/tickets/${t.ticket_id}`)} className="w-full text-left flex items-center justify-between px-4 py-3 hover:bg-white/5">
                <span className="text-sm"><span className="font-mono text-xs text-zinc-500 mr-2">#{t.ticket_number}</span>{t.subject}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded border ${STATUS_COLOR[t.status]}`}>{t.status}</span>
              </button>
            ))}
            {tickets.length === 0 && <div className="p-6 text-center text-sm text-zinc-500">No tickets</div>}
          </div>
        </TabsContent>

        <TabsContent value="time" className="mt-4">
          <div className="rounded-xl border border-white/10 bg-zinc-900/60 divide-y divide-white/5">
            {time.map((e) => (
              <div key={e.entry_id} className="flex items-center justify-between px-4 py-3 text-sm">
                <div><div>{e.description || "(no description)"}</div><div className="text-xs text-zinc-500">{fmtDate(e.started_at)} · {e.source} · actual {fmtDuration(e.actual_seconds)}</div></div>
                <span className={`text-[10px] px-1.5 py-0.5 rounded border ${BILLING_COLOR[e.billing_status]}`}>{e.billing_status}</span>
              </div>
            ))}
            {time.length === 0 && <div className="p-6 text-center text-sm text-zinc-500">No time entries</div>}
          </div>
        </TabsContent>

        <TabsContent value="sessions" className="mt-4">
          <div className="rounded-xl border border-white/10 bg-zinc-900/60 divide-y divide-white/5">
            {sessions.map((s) => (
              <button
                key={s.session_id}
                data-testid={`customer-session-${s.session_id}`}
                onClick={() => setDetailSid(s.session_id)}
                className="w-full text-left flex items-center justify-between px-4 py-3 text-sm hover:bg-white/5 transition-colors"
              >
                <div><div>{s.machine_name}</div><div className="text-xs text-zinc-500">{fmtDate(s.ended_at)} · connection {fmtDuration(s.duration_seconds)} · reconnects {s.reconnect_count}</div></div>
                <span className={`text-[10px] px-1.5 py-0.5 rounded border ${s.review_status === "reviewed" ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10" : "text-amber-400 border-amber-500/30 bg-amber-500/10"}`}>{s.review_status}</span>
              </button>
            ))}
            {sessions.length === 0 && <div className="p-6 text-center text-sm text-zinc-500">No remote sessions</div>}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="bg-zinc-900 border-white/10 max-w-lg" data-testid="customer-assign-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">Assign a computer to {c.company_name}</DialogTitle>
            <DialogDescription className="text-zinc-400 text-sm">
              Pick a computer to link to this customer. Computers already assigned elsewhere show their current customer — reassigning is explicit and never silent.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto rounded-xl border border-white/10 bg-black/30 divide-y divide-white/5">
            {allMachines.length === 0 && <div className="p-6 text-center text-sm text-zinc-500">No computers found</div>}
            {allMachines.map((m) => {
              const here = m.customer_id === cid;
              const other = m.customer_id && !here;
              return (
                <div key={m.machine_id} data-testid={`assign-pick-${m.machine_id}`} className="flex items-center gap-2 px-4 py-3 text-sm">
                  <Desktop size={16} className="text-zinc-400" />
                  <div className="min-w-0">
                    <div className="truncate">{m.name}</div>
                    {other && <div className="text-[11px] text-amber-400">Currently: {m.customer_name}</div>}
                    {here && <div className="text-[11px] text-emerald-400">Assigned to this customer</div>}
                  </div>
                  <div className="ml-auto">
                    {here ? (
                      <Button data-testid={`assign-pick-remove-${m.machine_id}`} variant="ghost" size="sm" disabled={busyMid === m.machine_id} onClick={() => setMachineCustomer(m, null)} className="h-7 px-2 text-xs text-zinc-400 hover:text-red-400">Remove</Button>
                    ) : (
                      <Button data-testid={`assign-pick-assign-${m.machine_id}`} size="sm" disabled={busyMid === m.machine_id} onClick={() => setMachineCustomer(m, cid)} className="h-7 px-3 text-xs bg-blue-600 hover:bg-blue-500">{other ? "Reassign" : "Assign"}</Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      <SessionDetailDialog
        open={!!detailSid}
        onOpenChange={(v) => !v && setDetailSid(null)}
        sessionId={detailSid}
        onUpdated={loadSessions}
      />
    </div>
  );
}
